import type { TaxMode } from "@/api/AppApi/services/company/interfaces/tax";
import type { IOrderDetailsItem } from "@/api/AppApi/services/order/interfaces/order-interfaces";
import type { IExchangeState } from "@/hooks/exchangeAtom";

/** Prefer saved amounts; legacy invoices only supplied saved tax rates. */
export const getOrderItemTax = (
  item: Pick<IOrderDetailsItem, "price" | "tax" | "taxes">,
  mode: TaxMode = "exclusive",
): number => {
  if (item.tax != null) return item.tax;
  const rate = item.taxes?.reduce((sum, tax) => sum + tax.rate, 0) ?? 0;
  return mode === "inclusive"
    ? (item.price * rate) / (100 + rate)
    : (item.price * rate) / 100;
};

export const getExchangeTotals = (
  exchange?: Pick<
    IExchangeState,
    "items" | "shippingCharge" | "tax_mode" | "enable_taxes"
  > | null,
) => {
  const subtotal = exchange?.items.reduce(
    (sum, item) => sum + item.price * item.quantity, 0,
  ) ?? 0;
  const tax = exchange?.enable_taxes === false
    ? 0
    : exchange?.items.reduce(
        (sum, item) => sum + (item.tax ?? 0) * item.quantity, 0,
      ) ?? 0;
  const itemTotal = subtotal + (exchange?.tax_mode === "inclusive" ? 0 : tax);
  const shipping = exchange?.shippingCharge ?? 0;
  return { subtotal, tax, itemTotal, shipping, credit: itemTotal + shipping };
};

/** Round only the locally calculated balance, never the backend cart total. */
export const getExchangePayableTotal = (
  total: number,
  exchange?: IExchangeState | null,
  fee = 0,
) => {
  if (!exchange?.items.length) return total;
  const balance = total + fee - getExchangeTotals(exchange).credit;
  const digits = exchange.currency?.decimal_digits;
  return digits == null ? balance : Number(balance.toFixed(digits)) || 0;
};
